/**
 * Theme toggle for static pages — respects system preference, persists choice.
 */

import { useState, useEffect } from 'react';

type Theme = 'light' | 'dark' | 'system';

function getSystemPreference(): boolean {
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function resolveTheme(theme: Theme): boolean {
  if (theme === 'system') return getSystemPreference();
  return theme === 'dark';
}

export function useStaticPageTheme() {
  const [theme, setTheme] = useState<Theme>(() => {
    const stored = localStorage.getItem('skjv-static-theme');
    return (stored as Theme) || 'system';
  });

  const isDark = resolveTheme(theme);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDark);
  }, [isDark]);

  useEffect(() => {
    localStorage.setItem('skjv-static-theme', theme);
  }, [theme]);

  // Listen for system changes when in system mode
  useEffect(() => {
    if (theme !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => document.documentElement.classList.toggle('dark', mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [theme]);

  return { theme, setTheme, isDark };
}

const ICONS: Record<Theme, string> = {
  light: '\u2600',
  dark: '\u263E',
  system: '\u25D0',
};

const LABELS: Record<Theme, string> = {
  light: 'Light',
  dark: 'Dark',
  system: 'System',
};

const CYCLE: Theme[] = ['light', 'dark', 'system'];

export function ThemeToggle({ theme, setTheme }: { theme: Theme; setTheme: (t: Theme) => void }) {
  const next = () => {
    const idx = CYCLE.indexOf(theme);
    setTheme(CYCLE[(idx + 1) % CYCLE.length]);
  };

  return (
    <button className="theme-toggle" onClick={next} title={`Theme: ${LABELS[theme]}`}>
      <span className="theme-toggle-icon">{ICONS[theme]}</span>
      <span className="theme-toggle-label">{LABELS[theme]}</span>
    </button>
  );
}
